import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShieldWarning, Brain, MagnifyingGlass, Gauge } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function FraudDetection() {
  const [scanning, setScanning] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [hours, setHours] = useState(24);
  const [affiliateId, setAffiliateId] = useState('');

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post('/fraud/scan', { time_range_hours: hours, affiliate_id: affiliateId || null });
      setScanResults(data);
      toast.success(`Scan complete: ${data.alerts_found} alerts found`);
    } catch (err) {
      toast.error('Scan failed');
    } finally { setScanning(false); }
  };

  const runAI = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post('/fraud/ai-analyze', { time_range_hours: hours, affiliate_id: affiliateId || null });
      setAiAnalysis(data);
      toast.success('AI analysis complete');
    } catch (err) {
      toast.error('AI analysis failed');
    } finally { setAnalyzing(false); }
  };

  const severityColor = (s) => ({ critical: 'bg-red-500/20 text-red-400', high: 'bg-red-500/10 text-red-400', medium: 'bg-amber-500/10 text-amber-400', low: 'bg-zinc-700/30 text-zinc-400' }[s] || '');

  return (
    <div className="space-y-6" data-testid="fraud-detection-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <ShieldWarning size={32} className="text-red-400" weight="duotone" />
          Fraud Detection Engine
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Rule-based scanning + GPT-5.2 deep analysis</p>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="text-xs text-zinc-500 mb-1 block">Affiliate ID (optional)</label>
              <Input data-testid="fraud-affiliate-id" value={affiliateId} onChange={e => setAffiliateId(e.target.value)} placeholder="Leave empty for network-wide scan" className="bg-zinc-950 border-zinc-800 text-zinc-100" />
            </div>
            <div className="w-32">
              <label className="text-xs text-zinc-500 mb-1 block">Time Range</label>
              <select data-testid="fraud-hours" value={hours} onChange={e => setHours(Number(e.target.value))} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-100 text-sm">
                <option value={6}>6 hours</option>
                <option value={24}>24 hours</option>
                <option value={72}>72 hours</option>
                <option value={168}>7 days</option>
              </select>
            </div>
            <div className="flex gap-2 items-end">
              <Button data-testid="run-scan-btn" onClick={runScan} disabled={scanning} className="bg-amber-600 hover:bg-amber-700 text-white gap-2">
                {scanning ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <MagnifyingGlass size={16} />}
                {scanning ? 'Scanning...' : 'Rule Scan'}
              </Button>
              <Button data-testid="run-ai-btn" onClick={runAI} disabled={analyzing} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
                {analyzing ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Brain size={16} />}
                {analyzing ? 'Analyzing...' : 'AI Analysis'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {scanResults && (
        <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-amber-500">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base text-white flex items-center gap-2"><Gauge size={18} className="text-amber-400" /> Rule-based Scan Results ({scanResults.alerts_found} alerts)</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(scanResults.alerts || []).map((a, i) => (
                <div key={i} className="p-3 bg-zinc-950 rounded-lg border border-zinc-800 flex items-center gap-3" data-testid={`scan-alert-${i}`}>
                  <Badge className={`${severityColor(a.severity)} text-[10px] shrink-0`}>{a.severity}</Badge>
                  <div className="flex-1">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{a.rule}</span>
                    <p className="text-xs text-zinc-300">{a.details}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="w-12 h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${a.score > 0.7 ? 'bg-red-500' : a.score > 0.4 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${a.score * 100}%` }} />
                    </div>
                    <span className="text-[10px] text-zinc-500">{(a.score * 100).toFixed(0)}%</span>
                  </div>
                </div>
              ))}
              {scanResults.alerts?.length === 0 && <p className="text-zinc-500 text-sm text-center py-4">No fraud patterns detected in the selected timeframe.</p>}
            </div>
          </CardContent>
        </Card>
      )}

      {aiAnalysis && (
        <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-blue-500">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base text-white flex items-center gap-2"><Brain size={18} className="text-blue-400" /> GPT-5.2 Deep Analysis</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-zinc-950 rounded-lg p-3"><p className="text-[10px] text-zinc-500 uppercase">Total Clicks</p><p className="text-lg font-bold text-blue-400 font-heading">{(aiAnalysis.total_clicks || 0).toLocaleString()}</p></div>
              <div className="bg-zinc-950 rounded-lg p-3"><p className="text-[10px] text-zinc-500 uppercase">Flagged Fraud</p><p className="text-lg font-bold text-red-400 font-heading">{aiAnalysis.fraud_clicks || 0}</p></div>
              <div className="bg-zinc-950 rounded-lg p-3"><p className="text-[10px] text-zinc-500 uppercase">Rule Alerts</p><p className="text-lg font-bold text-amber-400 font-heading">{aiAnalysis.rule_alerts?.length || 0}</p></div>
            </div>
            <div className="prose prose-invert prose-sm max-w-none" data-testid="ai-fraud-response">
              <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap bg-zinc-950 rounded-lg p-4 border border-zinc-800">{aiAnalysis.ai_analysis}</div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
